/**
 * TheoremMetaStrip — compact metadata row for an SSM explainer.
 *
 * Displays the canonical pieces an SSM has independent of its narrative
 * tier: code, structure, mode, generator, seam type, and the bridge
 * governance artifact. Mirrors the per-theorem seam taxonomy in
 * `theory/structural/SEAM-THEORY.md`.
 *
 * Mode and generator colors are derived from `mode` and `generator` to
 * eliminate the risk of those props drifting from their visual treatment.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import type { GeneratorCode, NeutralizationModeName } from "@csk/core";
import { FONT, GENERATOR_COLOR, GRAY, MODE_COLOR } from "../../design-system/tokens.js";

export type TheoremMode = NeutralizationModeName;

export interface TheoremMetaStripProps {
  readonly theoremName: string;
  readonly code: string;
  readonly structure: string;
  readonly mode: TheoremMode;
  readonly generator: GeneratorCode;
  readonly seamType: string;
  readonly bridgeArtifact: string;
  readonly theoremColor: string;
  readonly washColor: string;
}

export const TheoremMetaStrip = React.memo(function TheoremMetaStrip({
  theoremName,
  code,
  structure,
  mode,
  generator,
  seamType,
  bridgeArtifact,
  theoremColor,
  washColor,
}: TheoremMetaStripProps): React.JSX.Element {
  const modeStyle = MODE_COLOR[mode];
  const generatorStyle = GENERATOR_COLOR[generator];
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "0.375rem",
        padding: "0.5rem 0.75rem",
        backgroundColor: washColor,
        borderRadius: "0.5rem",
        fontFamily: FONT.body,
        fontSize: "0.75rem",
      }}
      aria-label={`${theoremName} metadata`}
    >
      <Chip label={code} color={theoremColor} bold title={theoremName} />
      <Chip label={structure} color={GRAY[700]} />
      <Chip label={modeStyle.label} color={modeStyle.dim} />
      <Chip label={`${generator} ${generatorStyle.label}`} color={generatorStyle.primary} />
      <Chip label={`seam: ${seamType}`} color={GRAY[700]} />
      <Chip label={`bridge: ${bridgeArtifact}`} color={GRAY[700]} />
    </div>
  );
});

interface ChipProps {
  readonly label: string;
  readonly color: string;
  readonly bold?: boolean;
  readonly title?: string;
}

function Chip({ label, color, bold = false, title }: ChipProps): React.JSX.Element {
  return (
    <span
      title={title}
      style={{
        padding: "0.15rem 0.5rem",
        borderRadius: "9999px",
        backgroundColor: "white",
        color,
        fontSize: "0.6875rem",
        fontWeight: bold ? 700 : 600,
        border: `1px solid ${color}33`,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}
